import React, { useRef, useState } from 'react';

// Étiquette en kCHF (cohérente entre l'axe Y et les points de la courbe).
const kLabel = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${Math.round(v)}`);

// Large interactive sparkline. Each of the 12 monthly values is a marker
// that can be dragged vertically. Mouseup commits the new monthly_values
// to the parent. Pure SVG — no chart library at this scale.
//
// Y range : 0 → ceiling, where ceiling = max(1.15 × current max, 10 000)
// so empty lines still show a usable canvas and drags never feel cramped.
// While dragging we freeze the ceiling at the start value, then on commit
// the next render picks up a fresh ceiling if the drag pushed past it.

const VIEW_W = 720;
const VIEW_H = 220;
const PAD_TOP = 28;
const PAD_BOTTOM = 28;
const PAD_LEFT = 72; // marge gauche élargie : place pour l'axe Y sans collision avec la courbe
const PAD_RIGHT = 12;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function chooseCeiling(values, minCeiling = 10000, roundTo = 100) {
  const max = Math.max(0, ...values.map(num));
  return Math.max(Math.ceil((max * 1.15) / roundTo) * roundTo, minCeiling);
}

function makeYTicks(ceiling, count = 4) {
  const step = ceiling / count;
  return [...Array(count + 1)].map((_, i) => Math.round(step * i));
}

// Compute the candidate values during a drag. When totalLocked, the diff
// at the dragged index is redistributed proportionally across the other
// months ; clamped to [0, …] to avoid negative values (any leftover is
// absorbed by capping the dragged value itself).
function computeNextValues(initialValues, idx, currentValue, totalLocked) {
  if (!totalLocked) {
    const next = [...initialValues];
    next[idx] = Math.max(0, Math.round(currentValue));
    return next;
  }
  const oldVal = initialValues[idx];
  const diff = currentValue - oldVal;
  const othersTotal = initialValues.reduce(
    (s, v, j) => (j === idx ? s : s + v),
    0
  );
  if (othersTotal <= 0) {
    // No room to redistribute, total grows.
    const next = [...initialValues];
    next[idx] = Math.max(0, Math.round(currentValue));
    return next;
  }
  // If diff > othersTotal, others would go negative ; cap.
  if (diff > othersTotal) {
    const capped = oldVal + othersTotal;
    return initialValues.map((v, j) => (j === idx ? Math.round(capped) : 0));
  }
  // Proportional redistribution.
  const out = initialValues.map((v, j) =>
    j === idx ? Math.round(currentValue) : Math.max(0, Math.round(v - (diff * v) / othersTotal))
  );
  // Tiny rounding residue on total : we accept it (rounding noise).
  return out;
}

const SAGE = '#9DC076';   // part prestation
const AMBER = '#D4A85A';  // part marchandise

export default function EditableSparkline({
  values,
  color = '#2B203E',
  monthLabels = [],
  disabled = false,
  totalLocked = false,
  // mode = 'prestation' (courbe) | 'mix' (barres empilées + courbe facturation)
  //      | 'marchandise' (barres facturation + courbe profit à encaisser)
  mode = 'prestation',
  goodsPct = 0,
  cogsRate = 0,
  // Généralisation : nb de points (défaut 12 mois), plancher d'échelle (défaut
  // 10'000 pour du CHF ; petit pour des jours/unités), format des étiquettes.
  count = 12,
  minCeiling = 10000,
  roundTo = 100,
  fmtVal = kLabel,
  // bandCenter : points centrés dans des bandes (i+0.5)/n — pour s'aligner sur une
  // grille de cases de même nombre de colonnes. Défaut = points bord-à-bord.
  bandCenter = false,
  onCommit,
}) {
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);
  // drag : { idx, currentValue, initialValues, ceiling }

  const n = Math.max(2, count);
  const safeValues = (values || []).slice(0, n).map(num);
  while (safeValues.length < n) safeValues.push(0);

  const displayValues = drag
    ? computeNextValues(drag.initialValues, drag.idx, drag.currentValue, totalLocked)
    : safeValues;

  const ceiling = drag ? drag.ceiling : chooseCeiling(safeValues, minCeiling, roundTo);
  const innerW = VIEW_W - PAD_LEFT - PAD_RIGHT;
  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM;
  const step = innerW / (n - 1);

  const valueToY = (v) => PAD_TOP + innerH - (Math.min(Math.max(v, 0), ceiling) / ceiling) * innerH;
  const idxToX = bandCenter ? (i) => PAD_LEFT + (i + 0.5) * (innerW / n) : (i) => PAD_LEFT + i * step;

  function startDrag(idx, e) {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();

    const ceilingAtStart = chooseCeiling(safeValues, minCeiling, roundTo);
    const initialValues = [...safeValues];
    const startValue = initialValues[idx];

    setDrag({ idx, currentValue: startValue, initialValues, ceiling: ceilingAtStart });

    function onMove(ev) {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const yPixel = ev.clientY - rect.top;
      const yView = (yPixel / rect.height) * VIEW_H;
      const yInChart = Math.min(Math.max(yView - PAD_TOP, 0), innerH);
      const value = Math.max(0, Math.round((1 - yInChart / innerH) * ceilingAtStart));
      setDrag((prev) => (prev ? { ...prev, currentValue: value } : prev));
    }

    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setDrag((curr) => {
        if (!curr) return null;
        const next = computeNextValues(
          curr.initialValues,
          curr.idx,
          curr.currentValue,
          totalLocked
        );
        // Only commit if anything actually changed.
        const changed = next.some((v, j) => v !== curr.initialValues[j]);
        if (changed) onCommit(next);
        return null;
      });
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // La courbe : facturation (prestation/mix) ou profit à encaisser (marchandise).
  const goods = Math.min(Math.max(Number(goodsPct) || 0, 0), 1);
  const cogs = Math.min(Math.max(Number(cogsRate) || 0, 0), 1);
  const lineVals = mode === 'marchandise'
    ? displayValues.map((v) => v * (1 - cogs))
    : displayValues;
  const showBars = mode === 'mix' || mode === 'marchandise';
  const showArea = mode !== 'mix';
  const baselineY = PAD_TOP + innerH;
  const barW = step * 0.55;

  const points = lineVals.map((v, i) => [idxToX(i), valueToY(v)]);
  const polyline = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const yTicks = makeYTicks(ceiling, 4);

  return (
    <div className="billing-sparkline">
      <svg
        ref={svgRef}
        className="billing-sparkline__svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: VIEW_H }}
      >
        {/* Y grid + axis labels */}
        {yTicks.map((tickValue, i) => {
          const y = valueToY(tickValue);
          return (
            <g key={i}>
              <line
                x1={PAD_LEFT}
                y1={y}
                x2={VIEW_W - PAD_RIGHT}
                y2={y}
                stroke="#EDE8DF"
                strokeDasharray="3 3"
              />
              <text
                x={PAD_LEFT - 12}
                y={y + 3}
                textAnchor="end"
                fontSize="10"
                fill="#78716C"
                fontFamily="JetBrains Mono, monospace"
              >
                {fmtVal(tickValue)}
              </text>
            </g>
          );
        })}

        {/* Barres : facturation (marchandise) ou empilées prestation/marchandise (mix) */}
        {showBars && displayValues.map((v, i) => {
          const x = idxToX(i) - barW / 2;
          if (mode === 'marchandise') {
            const yTop = valueToY(v);
            return v > 0 ? (
              <rect key={i} x={x} y={yTop} width={barW} height={baselineY - yTop}
                fill={AMBER} fillOpacity="0.7" />
            ) : null;
          }
          // mix : empilé (prestation en bas, marchandise au-dessus)
          const prest = v * (1 - goods);
          const yPrest = valueToY(prest);
          const yTotal = valueToY(v);
          return (
            <g key={i}>
              {prest > 0 && (
                <rect x={x} y={yPrest} width={barW} height={baselineY - yPrest}
                  fill={SAGE} fillOpacity="0.7" />
              )}
              {v - prest > 0 && (
                <rect x={x} y={yTotal} width={barW} height={yPrest - yTotal}
                  fill={AMBER} fillOpacity="0.7" />
              )}
            </g>
          );
        })}

        {/* Area under the curve */}
        {showArea && (
          <path
            d={
              `M ${points[0][0].toFixed(1)},${(PAD_TOP + innerH).toFixed(1)} ` +
              points.map(([x, y]) => `L ${x.toFixed(1)},${y.toFixed(1)}`).join(' ') +
              ` L ${points[points.length - 1][0].toFixed(1)},${(PAD_TOP + innerH).toFixed(1)} Z`
            }
            fill={color}
            fillOpacity="0.12"
          />
        )}

        {/* The line itself */}
        <polyline
          points={polyline}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Markers and value labels */}
        {displayValues.map((v, i) => {
          const x = idxToX(i);
          const y = valueToY(v);
          const isDragging = drag?.idx === i;
          return (
            <g key={i}>
              {/* Larger transparent hit area for easier grabbing */}
              <circle
                cx={x}
                cy={y}
                r="11"
                fill="transparent"
                style={{ cursor: disabled ? 'default' : 'ns-resize' }}
                onMouseDown={(e) => startDrag(i, e)}
              />
              <circle
                cx={x}
                cy={y}
                r={isDragging ? 6 : 5}
                fill="white"
                stroke={color}
                strokeWidth={isDragging ? 2.5 : 2}
                style={{ pointerEvents: 'none' }}
              />
              {v > 0 && (
                <text
                  x={x}
                  y={Math.max(y - 10, PAD_TOP - 6)}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight={isDragging ? 700 : 500}
                  fill={isDragging ? color : '#44403C'}
                  fontFamily="JetBrains Mono, monospace"
                  style={{ pointerEvents: 'none' }}
                >
                  {fmtVal(v)}
                </text>
              )}
            </g>
          );
        })}

        {/* X axis month labels */}
        {monthLabels.slice(0, n).map((label, i) => (
          <text
            key={i}
            x={idxToX(i)}
            y={VIEW_H - 8}
            textAnchor="middle"
            fontSize="11"
            fill="#78716C"
          >
            {label}
          </text>
        ))}
      </svg>
    </div>
  );
}
